<template>
  <div
    class="prevent-select"
    @contextmenu.stop.prevent="contextMenuClick"
    @click.ctrl.prevent="toggleItemSelection(false)"
    @click.shift.prevent="toggleItemSelection(true)"

    :draggable="isDraggableItem(item, asSearchResult)"
    @dragstart="onDragStart($event, item)"
    @dragend="onDragEnd($event, item)"

    role="listitem"
  >
    <!-- Select box -->
    <div data-cy="list-podItem-selectColumn">
      <PodItemSelect
        v-if="!asSearchResult"
        :pod="item"
        :isDeleted="isDeleted"
        @toggleItemSelection="toggleItemSelection"
        data-cy="list-podItem-selectBox"
      />
    </div>

    <!-- Thumbnail -->
    <div class="d-flex justify-center">
      <div class="" style="display: block;">

        <div style="min-height: 80px; display: flex;">
          <div v-if="showPreviews && (isArchived || isDeleted)">
            <PodThumbnail :item="item" compact :draggable="isDraggableItem(item, asSearchResult)" />
          </div>
          <router-link
            v-else-if="showPreviews"
            :to="{ path: `/pod/${item.id}` }"
            class="flex-grow-1"
            :draggable="isDraggableItem(item, asSearchResult)"
            :tabindex="-1"
            :aria-label="item.name"
          >
            <PodThumbnail :item="item" compact :draggable="isDraggableItem(item, asSearchResult)" />
          </router-link>

          <span v-else class="flex-grow-1 d-flex align-center justify-center">
            <v-icon size="x-large" class="text-secondary">mdi-file</v-icon>
          </span>
        </div>

        <div class="d-flex justify-space-between mx-auto" style="width: 120px; height: 21px; margin-top: -80px;">
          <div>
          </div>
          <div>
            <v-chip size="x-small" v-if="getFileType">{{ getFileType }}</v-chip>
          </div>
        </div>

        <div class="d-flex justify-space-between mx-auto" style="width: 120px; height: 21px; margin-top: 38px;">
          <div>
            <v-chip size="x-small" v-if="getDuration">{{ videoDuration(getDuration) }}</v-chip>
          </div>
          <div>
            <v-chip size="x-small" v-if="commentsCount > 0">
              <v-icon size="x-small" class="pr-1">mdi-comment-outline</v-icon>
              {{ commentsCount }}
            </v-chip>
          </div>
        </div>

      </div>
    </div>


    <!-- Pod Name -->
    <div class="d-flex flex-column justify-center align-baseline" data-cy="list-podItem-nameColumn">
      <div class="align-self-start" style="max-width: 100%">
        <v-tooltip location="bottom" :open-delay="200" :aria-label="item.name">
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="text-truncate"
              style="width: 100%"
              data-cy="list-podItem-name"
            >
              <router-link
                v-if="!isDeleted && !(isArchived && $canControlNodeAndPod())"
                v-bind:is="isArchived ? 'span' : 'router-link'"
                v-bind:to="{ path: `/pod/${item.id}` }"
                class="text-truncate"
                style="display: inline-block; max-width: 100%; min-height: 24px; line-height: 24px;"
                :draggable="isDraggableItem(item, asSearchResult)"
                :aria-label="$t('routes.node.a11y.openFile', { name: item.name })"
              >
                {{ item.name }}
              </router-link>
              <span v-else class="text-truncate">
                <b>{{ item.name }}</b>
              </span>
            </div>
          </template>
          <span>{{ item.name }}</span>
        </v-tooltip>

        <div
          v-if="
            uploadingFile(item) &&
            uploadingFile(item)?.status === UPLOADSTATUSES.UPLOADING
          "
          data-cy="list-podItem-statusUploading"
        >
          <span>
            {{
              $t("routes.node.statusUploading", [
                uploadingFile(item)?.progress?.percentage,
              ])
            }}
          </span>
          <v-progress-linear
            :model-value="uploadingFile(item)?.progress.percentage"
          />
        </div>

        <div
          v-if="
            uploadingFile(item) &&
            uploadingFile(item)?.status === UPLOADSTATUSES.FINISHED &&
            !item?.statemessage && item?.status?.status !== 'completed'
          "
          data-cy="list-podItem-uploadFinished"
        >
          {{ $t("routes.node.statusFinished") }}
        </div>

        <div
          v-if="uploadingFile(item) && uploadingFile(item)?.status === UPLOADSTATUSES.ERROR"
          data-cy="list-podItem-uploadError"
        >
          {{ $t("routes.node.statusError") }}
        </div>
        <div
          v-if="
            uploadingFile(item) &&
            uploadingFile(item)?.status === UPLOADSTATUSES.WAITING
          "
          data-cy="list-podItem-statusWaiting"
        >
          {{ $t("routes.node.statusWaiting") }}
        </div>

        <div v-if="item && item?.statemessage" data-cy="list-podItem-stateMessage">
          <span v-if="typeof item?.statemessage == 'object'">
            {{ $replaceT(item.statemessage.code, item.statemessage.params, item.statemessage.content) }}
          </span>
          <span v-else>
            {{ item.statemessage }}
          </span>
        </div>
        <!-- In case the state message wasn't set, compute one based on current pod status -->
        <div v-else-if="!uploadingFile(item) && statusStateMessage">
          {{ $replaceT(statusStateMessage.code, statusStateMessage.params) }}
        </div>

        <div v-if="isDeleted && item?.deletedby && !hasRetentionInfo" class="font-italic" data-cy="list-podItem-deletedByName">
          {{ $t("common.deletedBy") }} {{ item.deletedby.firstname }} {{ item.deletedby.lastname }}
        </div>
        <div v-else-if="isDeleted && !hasRetentionInfo" class="font-italic" data-cy="list-podItem-deletedByName">
          {{ $t("common.deletedBy") }} {{ $t("common.deletedUser") }}
        </div>

        <div>
          <v-chip v-if="item.teamonly === accessType.TEAM_ONLY"
                  data-cy="list-podItem-accessType-teamOnly" size="x-small" class="mr-1">
            {{ $t('routes.node.accessTypeTeamOnly') }}
          </v-chip>
          <v-chip v-if="item.external" size="x-small" data-cy="list-podItem-externalUploaded">
            {{ $t("routes.node.externallySourced") }}
          </v-chip>

          <v-chip v-if="item.badgestatus" size="x-small" data-cy="list-podItem-podStatus">
            {{ getBadgeStatus }}
          </v-chip>

          <v-chip v-if="item.archived === archiveStatus.ARCHIVED" size="x-small" data-cy="list-podItem-archiveStatus-archived">
            {{ $t('routes.node.statusArchived') }}
          </v-chip>

          <v-chip v-if="item.archived === archiveStatus.PENDING" size="x-small" data-cy="list-podItem-archiveStatus-pending">
            {{ $t('routes.node.statusPendingUnarchive') }}
          </v-chip>

          <v-chip v-if="item.archived === archiveStatus.ARCHIVING" size="x-small" data-cy="list-podItem-archiveStatus-archiving">
            {{ $t('routes.node.statusArchiving') }}
          </v-chip>

          <v-chip v-if="item.archived === archiveStatus.UNARCHIVING" size="x-small" data-cy="list-podItem-archiveStatus-unarchiving">
            {{ $t('routes.node.statusUnarchiving') }}
          </v-chip>
        </div>

      </div>

      <!-- Search result metadata -->
      <PodItemSearchResults
        v-if="!isDeleted"
        :pod="item"
        class="align-self-start"
      />

      <!-- Recycle Bin Retention Period -->
      <RecycleBinListViewRetentionPeriod
        v-if="isDeleted"
        :pod="item"
        class="align-self-start"
      />
    </div>

    <!-- Size -->
    <div data-cy="list-podItem-sizeColumn">
      <div data-cy="list-podItem-size">
        <span v-if="item.asset">
          {{ formatSize(((item.size) ? item.size : 0), 2) }}
        </span>
        <span v-else-if="uploadingFile(item)">
          {{ formatSize(uploadingFile(item).meta.filesize, 2) }}
        </span>
      </div>
      <div v-if="!asSearchResult && getPodVersions.length > 1" data-cy="list-podItem-versions">
        <v-chip size="small">
          {{ $t("routes.node.versions", getPodVersions.length) }}
        </v-chip>
      </div>
    </div>

    <!-- Uploader -->
    <div data-cy="list-podItem-uploaderColumn">
      <div v-if="isDeleted">
        <small v-if="item.deletedat" data-cy="list-podItem-date">{{ isoToDatetime(item.deletedat) }}</small>
      </div>
      <div v-else style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
        <small data-cy="list-podItem-date">{{ isoToDatetime(item.createdat) }}</small>
        <br />
        <v-tooltip location="bottom" :open-delay="200" :aria-label="getUploaderName">
          <template v-slot:activator="{ props }">
            <span v-bind="props" data-cy="list-podItem-createdByName">
              {{ getUploaderName }}
            </span>
          </template>
          <span>{{ getUploaderName }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Notes -->
    <div data-cy="list-podItem-noteColumn">
      <PodItemNote :editable="!isDeleted" :mediaPod="item" data-cy="list-podItem-noteArea" />
    </div>

    <!-- Actions -->
    <div data-cy="list-podItem-actionsColumn">
      <v-btn
        @click="contextMenuBtnClick"
        width="24"
        height="24"
        rounded="circle"
        icon
        variant="flat"
        color="transparent"
        :aria-label="$t('routes.node.a11y.openActionsMenu', { name: item.name })"
      >
        <v-icon data-cy="list-podItem-actions-btn">mdi-dots-vertical</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useUploadStore } from '@/stores/UploadStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import { useRecycleBinStore } from '@/stores/RecycleBinStore.js';
import PodItemNote from "./PodItemNote.vue";
import PodItemSelect from "./PodItemSelect.vue";
import PodItemSearchResults from "./PodItemSearchresults.vue";
import RecycleBinListViewRetentionPeriod from "../../../recycle-bin/components/RecycleBinListViewRetentionPeriod.vue";
import PodThumbnail from '@/layouts2/components/PodThumbnail.vue';
import { formatSize, isoToDatetime, videoDuration } from '@/components/formatters.js';
import MovableNodePod from '@/components/mixin/MovableItem';

export default {
  name: "ListPodItem",
  mixins: [MovableNodePod],
  emits: ["toggleItemSelection", "showContextMenu"],
  components: {
    PodThumbnail,
    PodItemNote,
    PodItemSelect,
    PodItemSearchResults,
    RecycleBinListViewRetentionPeriod,
    // ArchiveSelectedDialog,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    isDeleted: {
      type: Boolean,
      default: false
    },
    showPreviews: {
      type: Boolean,
      default: true
    },
    // isArchived: {
    //   type: Boolean,
    //   default: false
    // }
  },

  data() {
    return {
      adobeMessage: "",
      commentsCount: 0,
    };
  },

  mounted() {
    this.updateCommentsCount();
  },

  methods: {
    isoToDatetime,
    videoDuration,
    formatSize,
    async updateCommentsCount() {
      if (!this.$canListPodService()) {
        return;
      }

      if (this.item.archived || this.isDeleted || this.asSearchResult) {
        this.commentsCount = 0;
        return;
      }

      if (this.item.commentsCount !== undefined) {
        this.commentsCount = this.item.commentsCount;
        return;
      }

      try {
        const podCommentStore = usePodCommentStore();
        const comments = await podCommentStore.getCommentsStateless(this.item.id);
        this.commentsCount = (Array.from(comments)).length;
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    },
    toggleItemSelection(holdingShift) {
      const payload = {
        holdingShift: holdingShift,
        item: this.item
      };

      this.$emit("toggleItemSelection", payload);
    },
    contextMenuClick(event) {
      const eventData = {
        contextMenuPod: this.item,
        contextMenuX: event.clientX,
        contextMenuY: event.clientY,
      };

      this.$emit('showContextMenu', eventData);
    },
    contextMenuBtnClick(event) {
      const eventData = {
        contextMenuPod: this.item,
        contextMenuX: event.clientX - event.offsetX + event.target.offsetWidth,
        contextMenuY: event.clientY - event.offsetY + event.target.offsetHeight,
      };

      this.$emit('showContextMenu', eventData);
    },
  },

  computed: {
    UPLOADSTATUSES() {
      const uploadStore = useUploadStore();
      return uploadStore.statuses;
    },
    isArchived() {
      return this.item.archived !== 0;
    },
    getPodVersions() {
      if (this.isDeleted) {
        const recycleBinStore = useRecycleBinStore();
        return recycleBinStore.getDeletedPodVersions(this.item.id);
      }

      const podStore = usePodStore();
      return podStore.getPodVersions(this.item.id);
    },
    badgeStatuses() {
      const commonStore = useCommonStore();
      return commonStore.podBadgeStatuses;
    },
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    accessType() {
      const commonStore = useCommonStore();
      return commonStore.accessType;
    },
    getBadgeStatus() {
      switch (this.item.badgestatus) {
        case this.badgeStatuses.NEW:          return this.$t('routes.pod.badgeStatusNew');
        case this.badgeStatuses.DRAFT:        return this.$t('routes.pod.badgeStatusDraft');
        case this.badgeStatuses.PRODUCTION:   return this.$t('routes.pod.badgeStatusProduction');
        case this.badgeStatuses.REVIEW:       return this.$t('routes.pod.badgeStatusReview');
        case this.badgeStatuses.CORRECTION:   return this.$t('routes.pod.badgeStatusCorrection');
        case this.badgeStatuses.FINAL:        return this.$t('routes.pod.badgeStatusFinal');
        default: return '';
      }
    },
    uploadingFile() {
      return (pod) => {
        const uploadStore = useUploadStore();
        const uploadingFiles = uploadStore.files;

        for (let i = 0; i < uploadingFiles.length; i++) {
          if (uploadingFiles[i].meta.fileuuid == pod.uuid) {
            return uploadingFiles[i];
          }
        }

        return null;
      };
    },
    getFileType() {
      if (
        this.item &&
        this.item.asset &&
        this.item.asset.raw &&
        this.item.asset.raw[0] &&
        this.item.asset.raw[0].filetype
      ) {
        return this.item.asset.raw[0].filetype;
      }

      return false;
    },
    getDuration() {
      if (
        this.item &&
        this.item.asset &&
        this.item.asset.web &&
        this.item.asset.web[0] &&
        this.item.asset.web[0].duration
      ) {
        return this.item.asset.web[0].duration;
      }

      return false;
    },
    getUploaderName() {
      if (this.item.jsondata?.uploaderName) {
        return `${this.$t("routes.node.externalUploader")}: ${this.item.jsondata.uploaderName}`;
      }

      if (this.item) {
        if (this.item.createdby) {
          return `${this.item.createdby.firstname} ${this.item.createdby.lastname}`;
        }

        return this.$t('common.deletedUser');
      }

      return this.$t("routes.node.unknownUploader");
    },
    statusStateMessage() {
      if (this.item && this.item.status && this.item.status.status !== 'completed') {
        const podStore = usePodStore();
        return podStore.getStatusStateMessage(this.item.id);
      }

      return null;
    },
    hasRetentionInfo() {
      return this.item?.retention && this.item.retention.startedAt && this.item.retention.period && this.item.retention.periodType;
    },
  }
};
</script>

<style>

</style>
